import type { HandleKind } from "../render/handles";
import type { OverlayChannel } from "../render/overlayChannel";
import type { SnapEngine } from "../snap/snapEngine";
import type { EditorStore } from "../store/createEditorStore";
import type { Point, ToolKind } from "../types";

/** What a tool can reach. Tools keep gesture-local state in closures and
 *  only touch the store when a gesture commits. */
export interface ToolContext {
  store: EditorStore;
  overlay: OverlayChannel;
  snap: SnapEngine;
  toWorld: (client: Point) => Point;
  /** Shape id under an event target, via `data-shape-id`. */
  hitShape: (target: EventTarget | null) => string | null;
  /** `data-handle` under an event target: a transform handle (`nw`,
   *  `rotate`, …) or a geometry handle (`vertex:2`, `wall-start`, …). */
  hitHandle: (target: EventTarget | null) => HandleKind | string | null;
  shapeNode: (id: string) => SVGGElement | null;
  /** Element under a client point. Events that follow a pointer capture
   *  (`dblclick`, `contextmenu`) are targeted at the svg, so hit-test
   *  this instead of `event.target` there. */
  elementAt: (client: Point) => Element | null;
  /** World units per screen pixel at the current zoom. */
  worldPerPixel: () => number;
  setCursor: (cursor: string | null) => void;
}

export interface Tool {
  kind: ToolKind;
  cursor: string;
  onPointerDown: (context: ToolContext, event: PointerEvent) => void;
  onPointerMove: (context: ToolContext, event: PointerEvent) => void;
  onPointerUp: (context: ToolContext, event: PointerEvent) => void;
  onDoubleClick?: (context: ToolContext, event: MouseEvent) => void;
  /** Return true when the key was consumed. */
  onKeyDown?: (context: ToolContext, event: KeyboardEvent) => boolean;
  /** Escape, tool switch or blur: drop any gesture in progress. */
  cancel: (context: ToolContext) => void;
  /** True while a multi-click drawing is open (right-click then finishes
   *  it instead of opening the context menu). */
  isBusy?: () => boolean;
}

/** Runs `frame` at most once per animation frame with the latest value. */
export const createFrameScheduler = <T>(frame: (value: T) => void) => {
  let pending: T | null = null;
  let handle: number | null = null;
  return {
    schedule: (value: T) => {
      pending = value;
      if (handle !== null) return;
      handle = requestAnimationFrame(() => {
        handle = null;
        if (pending !== null) frame(pending);
        pending = null;
      });
    },
    cancel: () => {
      if (handle !== null) cancelAnimationFrame(handle);
      handle = null;
      pending = null;
    }
  };
};
